<template>
  <GenericCard
    :locked="locked"
    has-delete
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="v$.section.$invalid"
    :dirty="v$.section.$anyDirty"
    :create="create"
    title="Previous Denial"
    :section="section"
    has-footer
    has-share
    :card-identifier="cardIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col
          cols="12"
        >
          <v-alert
            border="left"
            colored-border
            color="#2A3B4D"
            icon="mdi-information-outline"
            elevation="2"
            class="mb-6"
          >
            An applicant may be barred from applying for asylum if a previous application was denied by an immigration judge or the Board of Immigration Appeals.
          </v-alert>
        </v-col>
        <v-col cols="12">
          <h4>
            Previous application filed?
          </h4>
          <v-radio-group
            v-model="section.previous_application_filed"
            row
            class="mt-0"
          >
            <!-- eslint-disable vue/prefer-true-attribute-shorthand -->
            <v-radio
              label="Yes"
              :value="true"
            />
            <!-- eslint-enable -->
            <v-radio
              label="No"
              :value="false"
            />
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row v-if="section.previous_application_filed">
        <v-col
          cols="12"
          class="mb-3"
        >
          <h4>Outcome?</h4>
          <div
            key="1"
            class="d-inline-block d-inline-block mr-4"
          >
            <v-checkbox
              v-model="section.granted"
              hide-details
              class="mt-0"
            >
              <div slot="label">
                Granted
              </div>
            </v-checkbox>
          </div>
          <div
            key="2"
            class="d-inline-block mr-4"
          >
            <v-checkbox
              v-model="section.denied_by_uscis"
              hide-details
              class="mt-0"
            >
              <div slot="label">
                Denied by USCIS
              </div>
            </v-checkbox>
          </div>
          <div
            key="3"
            class="d-inline-block mr-4"
          >
            <v-checkbox
              v-model="section.denied_by_immigration_judge"
              hide-details
              class="mt-0"
            >
              <div slot="label">
                Denied by immigration judge
              </div>
            </v-checkbox>
          </div>
          <div
            key="4"
            class="d-inline-block mr-4"
          >
            <v-checkbox
              v-model="section.denied_by_bia"
              hide-details
              class="mt-0"
            >
              <div slot="label">
                Denied by BIA
              </div>
            </v-checkbox>
          </div>
          <div
            key="5"
            class="d-inline-block"
          >
            <v-checkbox
              v-model="section.denied_by_unknown"
              hide-details
              class="mt-0"
            >
              <div slot="label">
                Unknown
              </div>
            </v-checkbox>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-textarea
            v-model="section.narrative"
            auto-grow
            outlined
            autocomplete="off"
            label="Narrative"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { useVuelidate } from '@vuelidate/core'

import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'PreviousDenialsCard',
  components: { GenericCard },

  props: {
    item: {
      type: Object,
      required: true,
    },
  },

  setup () {
    return {
      v$: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      section: {},
    }
  },

  validations () {
    return {
      section: {},
    }
  },

  computed: {
    cardIdentifier () { return `previous-denials-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.pePreviousDenialsCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPePreviousDenialsCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.section.name})`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'pePreviousDenialsCardLock',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },
  },

  created () {
    if (!this.create) this.initPePreviousDenialsCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.pePreviousDenialsCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.pePreviousDenialsCardCreateUndo()
      else {
        return this.pePreviousDenialsCardDelete({ id: this.section.id, index: this.sectionIdentifier })
      }
    },

    async onCardRefresh () {
      return this.pePreviousDenialsCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.pePreviousDenialsCardUpdate({
        id: this.section.id,
        index: this.sectionIdentifier,
        section: this.section,
      })
    },

    async onSectionUnlock () {
      return this.setPePreviousDenialsCardLock({ index: this.sectionIdentifier, locked: false })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'pePreviousDenialsCardCreateUndo',
      'setPePreviousDenialsCardLock',

    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPePreviousDenialsCard',
      'pePreviousDenialsCardCreate',
      'pePreviousDenialsCardDelete',
      'pePreviousDenialsCardRefresh',
      'pePreviousDenialsCardUpdate',
    ]),
  },
}
</script>
